/**
 * Resize and JPEG-compress an image File to produce a base64 payload
 * suitable for inclusion in the Claude `image-upload` message format.
 *
 * The longest edge is capped at `maxDimension` (default 512 px). The
 * output is always JPEG at 0.7 quality — sufficient for Claude to
 * recognise content without unnecessary size (the full-quality asset is
 * stored separately via MCP).
 */
export async function compressImage(
    file: File,
    maxDimension = 512
): Promise<{ base64: string; mediaType: 'image/jpeg' }> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            let { naturalWidth: width, naturalHeight: height } = img;

            if (width > height && width > maxDimension) {
                height = Math.round((height * maxDimension) / width);
                width = maxDimension;
            } else if (height > maxDimension) {
                width = Math.round((width * maxDimension) / height);
                height = maxDimension;
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d')?.drawImage(img, 0, 0, width, height);

            const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
            const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, '');
            resolve({ base64, mediaType: 'image/jpeg' });
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Failed to load image for compression'));
        };

        img.src = objectUrl;
    });
}
